import { Component, Input, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  StoreIcon,
  TruckIcon,
  PackageIcon,
  BellIcon,
  BellOffIcon,
} from 'lucide-angular';
import type { Producto } from '../../../types/product.types';
import { StoreService } from '../../../services/store.service';
import { ProductTrackingService } from '../../../services/product-tracking.service';
import { NotificationService } from '../../../services/notification.service';
import { getSucursalesGroupedByCity } from '../../../utils/stores/relaciones-sucursales';

@Component({
  selector: 'allas-delivery-type-by-store',

  imports: [CommonModule, LucideAngularModule],
  template: `
    <!-- Debug Info -->
    <!-- <div class="text-xs text-gray-400 mb-2 p-1 bg-gray-50 rounded">
      Debug: SD={{ hasSameDayDelivery() }} | SP={{ hasStorePickup() }} | ND={{ hasNextDayDelivery() }}
    </div> -->

    <!-- Sin tienda seleccionada -->
    @if (!selectedStore()) {
    <div
      class="flex items-center gap-2 text-sm text-gray-600 space-y-2 fs-condensed"
    >
      <lucide-icon [name]="storeIcon" class="w-4 h-4 text-gray-500" />
      <span>
        <span class="underline">Selecciona una tienda</span>
        para ver disponibilidad de recogida
      </span>
    </div>
    }

    <!-- Con tienda seleccionada -->
    @if (selectedStore()) {
    <div class="space-y-2 text-sm fs-condensed">
      <!-- Banner de Inventario Limitado -->
      @if (hasNoInventory()) {
      <div class="space-y-2">
        <!-- Si ya tiene recordatorio -->
        @if (hasReminder()) {
        <div class="bg-green-50 border border-green-200 rounded-lg p-3">
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"
            >
              <lucide-icon [name]="bellIcon" class="w-4 h-4 text-green-600" />
            </div>
            <div class="flex-1">
              <p class="text-green-800 font-medium text-sm">
                ¡Recordatorio agregado!
              </p>
              <p class="text-green-600 text-xs">
                Te notificaremos cuando tengamos stock disponible.
              </p>
            </div>
            <button
              (click)="removeReminder($event)"
              class="text-green-600 hover:text-green-800 transition-colors"
              title="Quitar recordatorio"
            >
              <lucide-icon [name]="bellOffIcon" class="w-4 h-4" />
            </button>
          </div>
        </div>
        } @else {
        <!-- Si no tiene recordatorio -->
        <button
          (click)="addReminder($event)"
          class="w-full text-left bg-orange-50 border border-orange-200 rounded-lg p-3 cursor-pointer hover:bg-orange-100 transition-colors fs-condensed"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center"
            >
              <lucide-icon
                [name]="packageIcon"
                class="w-4 h-4 text-orange-600"
              />
            </div>
            <div class="flex-1">
              <p class="text-orange-800 font-medium text-sm">
                No hay stock, ¡Agrega un recordatorio!
              </p>
              <p class="text-orange-600 text-xs">
                Clic aquí para agregar un recordatorio para este producto.
              </p>
            </div>
          </div>
        </button>
        }
      </div>
      }

      <!-- Entrega gratis el mismo día -->
      @if (hasSameDayDelivery()) {
      <div class="flex items-center gap-4 fs-condensed">
        <lucide-icon [name]="truckIcon" class="w-4 h-4 text-gray-500" />
        <span>
          <span class="text-green-600 font-medium">¡Entrega gratis Hoy:</span>
          <span class="text-gray-500">
            No esperes más, haz tu pedido y recibelo hoy mismo.
          </span>
        </span>
      </div>
      }

      <!-- Recoger en tienda -->
      @if (hasStorePickup()) {
      <div class="flex items-center gap-4 fs-condensed">
        <lucide-icon [name]="storeIcon" class="w-4 h-4 text-gray-500" />
        <span>
          <span class="text-green-600 font-medium">Recoger en tienda:</span>
          <span class="text-gray-500">
            Puedes recoger tu pedido en:
            <span class="font-bold">{{ selectedStore()?.name }}</span>
          </span>
        </span>
      </div>
      }

      <!-- Envío gratis al día siguiente -->
      @if (hasNextDayDelivery() && (!hasStorePickup() && !hasSameDayDelivery()))
      {
      <div class="flex items-center gap-4 fs-condensed">
        <lucide-icon [name]="packageIcon" class="w-4 h-4 text-gray-500" />
        <span>
          <span class="text-green-600 font-medium">
            Entrega gratis mañana
          </span>
          <span class="text-gray-500">Haz tu pedido antes de las 12:00 PM</span>
        </span>
      </div>
      }
    </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class DeliveryTypeByStore {
  @Input() product!: Producto;

  private storeService = inject(StoreService);
  private productTrackingService = inject(ProductTrackingService);
  private notificationService = inject(NotificationService);

  readonly storeIcon = StoreIcon;
  readonly truckIcon = TruckIcon;
  readonly packageIcon = PackageIcon;
  readonly bellIcon = BellIcon;
  readonly bellOffIcon = BellOffIcon;

  readonly selectedStore = computed(() => this.storeService.getSelectedStore());

  // Convertir el observable de recordatorios a signal para reactividad automática
  private readonly remindersSignal = toSignal(
    this.productTrackingService.getReminders(),
    { initialValue: [] }
  );

  readonly hasReminder = computed(() => {
    const productId = this.getProductUniqueId(this.product);
    const reminders = this.remindersSignal();
    return reminders.some((reminder) => reminder.productId === productId);
  });

  readonly hasSameDayDelivery = computed(() => {
    if (!this.selectedStore() || !this.product?.inventarios) return false;

    const selectedStoreCode = this.selectedStore()?.id;
    if (!selectedStoreCode) return false;

    // Buscar si hay inventario en la tienda seleccionada
    const hasInventoryInSelectedStore = this.product.inventarios.some(
      (inv) => inv.sucursal === selectedStoreCode && inv.cantidad > 0
    );

    if (hasInventoryInSelectedStore) return true;

    // Buscar si hay inventario en sucursales de la misma ciudad
    const selectedStoreCity = this.getCityByStoreCode(selectedStoreCode);

    if (selectedStoreCity) {
      const cityStores = this.getStoresByCity(selectedStoreCity);

      const hasInventoryInCity = cityStores.some((storeCode) =>
        this.product.inventarios?.some(
          (inv) => inv.sucursal === storeCode && inv.cantidad > 0
        )
      );

      return hasInventoryInCity;
    }

    return false;
  });

  readonly hasStorePickup = computed(() => {
    if (!this.selectedStore() || !this.product?.inventarios) return false;

    const selectedStoreCode = this.selectedStore()?.id;
    if (!selectedStoreCode) return false;

    const hasPickup = this.product.inventarios.some(
      (inv) => inv.sucursal === selectedStoreCode && inv.cantidad > 0
    );

    return hasPickup;
  });

  readonly hasNextDayDelivery = computed(() => {
    if (!this.product?.inventarios) return false;

    const hasNextDay = this.product.inventarios.some((inv) => inv.cantidad > 0);

    return hasNextDay;
  });

  readonly hasNoInventory = computed(() => {
    if (!this.product?.inventarios) return true;

    const hasAnyStock = this.product.inventarios.some(
      (inv) => inv.cantidad > 0
    );
    return !hasAnyStock;
  });

  private getCityByStoreCode(storeCode: string): string | null {
    const cities = getSucursalesGroupedByCity();
    const city = cities.find((city) => city.sucursales.includes(storeCode));
    return city?.city || null;
  }

  private getStoresByCity(city: string): string[] {
    const cities = getSucursalesGroupedByCity();
    const cityData = cities.find((c) => c.city === city);
    return cityData?.sucursales || [];
  }

  addReminder(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const success = this.productTrackingService.addReminder(this.product);

    if (success) {
      this.notificationService.showSuccess(
        'Recordatorio agregado',
        'Te notificaremos cuando tengamos stock disponible'
      );
    } else {
      this.notificationService.showError(
        'Error al agregar recordatorio',
        'Este producto ya tiene un recordatorio activo'
      );
    }
  }

  removeReminder(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const productId = this.getProductUniqueId(this.product);
    const success = this.productTrackingService.removeReminder(productId);

    if (success) {
      this.notificationService.showSuccess(
        'Recordatorio removido',
        'El recordatorio ha sido eliminado exitosamente'
      );
    }
  }

  private getProductUniqueId(product: Producto): string {
    // Usar SKU si está disponible, sino usar part_number
    return product.sku?.trim() || product.part_number;
  }
}
